import {getEnv} from '@github-ui/client-env'
import {useSyncExternalStore} from 'react'

const empty: ReadonlySet<string> = new Set()
const scopes = new Map<string, ReadonlySet<string>>()
const listeners = new Set<() => void>()

export const UNSUBMITTED_CHAT_MESSAGE = 'This chat was not submitted. Start a new chat to continue.'

export function earlyTaskVisibilityScope(): string {
  const {login, copilotApiOverrideUrl, cmcApiUrl} = getEnv()
  return JSON.stringify([login, copilotApiOverrideUrl, cmcApiUrl])
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

function update(taskId: string, scope: string, hidden: boolean): void {
  const previous = scopes.get(scope) ?? empty
  if (previous.has(taskId) === hidden) return
  const next = new Set(previous)
  if (hidden) next.add(taskId)
  else next.delete(taskId)
  scopes.set(scope, next)
  for (const listener of listeners) listener()
}

// Keep discarded IDs hidden for this document's lifetime, including stale refetches after DELETE.
// This store holds visibility only; allocation and cleanup remain owned by the composer/runtime.
export function hideEarlyTask(taskId: string, scope: string): void {
  update(taskId, scope, true)
}

export function publishEarlyTask(taskId: string, scope: string): void {
  update(taskId, scope, false)
}

export function isEarlyTaskHidden(taskId: string): boolean {
  return scopes.get(earlyTaskVisibilityScope())?.has(taskId) ?? false
}

export function useHiddenEarlyTaskIds(): ReadonlySet<string> {
  const scope = earlyTaskVisibilityScope()
  return useSyncExternalStore(
    subscribe,
    () => scopes.get(scope) ?? empty,
    () => empty,
  )
}
